import React from 'react';
import {
  LineChart, Line,
  BarChart, Bar,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

/**
 * 图表渲染组件 - 使用Recharts库
 * @param {Object} section - 图表配置区块
 * @param {string} lang - 当前语言
 */
const ChartRenderer = ({ section, lang }) => {
  const { chartType, data, config = {}, title } = section;
  const chartTitle = title && (typeof title === 'object' ? title[lang] : title);

  // 默认颜色方案
  const defaultColors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
      ...config
    };

    switch (chartType) {
      case 'line':
        return renderLineChart(commonProps);
      
      case 'bar':
        return renderBarChart(commonProps);
      
      case 'area':
        return renderAreaChart(commonProps);
      
      case 'pie':
        return renderPieChart(commonProps);
      
      default:
        console.warn(`Unknown chart type: ${chartType}`);
        return <p>Unsupported chart type: {chartType}</p>;
    }
  };

  const renderLineChart = (props) => {
    const { dataKeys = [], colors = defaultColors } = config;

    return (
      <LineChart {...props}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        {dataKeys.map((key, index) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={colors[index] || defaultColors[index % defaultColors.length]}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    );
  };

  const renderBarChart = (props) => {
    const { dataKeys = [], colors = defaultColors } = config;

    return (
      <BarChart {...props}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        {dataKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            fill={colors[index] || defaultColors[index % defaultColors.length]}
          />
        ))}
      </BarChart>
    );
  };

  const renderAreaChart = (props) => {
    const { dataKeys = [], colors = defaultColors } = config;

    return (
      <AreaChart {...props}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        {dataKeys.map((key, index) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stroke={colors[index] || defaultColors[index % defaultColors.length]}
            fill={colors[index] || defaultColors[index % defaultColors.length]}
            fillOpacity={0.6}
          />
        ))}
      </AreaChart>
    );
  };

  const renderPieChart = (props) => {
    const { colors = defaultColors, innerRadius = 0, outerRadius = 100 } = config;

    return (
      <PieChart {...props}>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    );
  };

  return (
    <div className="chart-container">
      {chartTitle && <h3 className="chart-title">{chartTitle}</h3>}
      <ResponsiveContainer width="100%" height={400}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default ChartRenderer;
